import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { policyContent } from "@/data/tier1PolicyContent";
import RichTextEditor from "@/components/RichTextEditor";

interface PolicySection {
  id: string;
  section_number: number;
  title: string;
  description: string;
  content: {
    subsections: {
      title: string;
      content: string;
    }[];
  };
}

interface SectionFormData {
  section_number: number;
  title: string;
  description: string;
  subsections: {
    title: string;
    content: string;
  }[];
}

export default function Tier1PolicyEditor() {
  const [sections, setSections] = useState<PolicySection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingSection, setEditingSection] = useState<PolicySection | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const { staffMember, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const form = useForm<SectionFormData>({
    defaultValues: {
      section_number: 1,
      title: "",
      description: "",
      subsections: [{ title: "", content: "" }],
    },
  });

  // Check admin or superadmin access - wait for auth to load
  useEffect(() => {
    // Don't check permissions while still loading auth
    if (authLoading) {
      return;
    }
    
    // Once auth is loaded, check if user is admin or superadmin
    if (!staffMember || (staffMember.role_level !== "superadmin" && staffMember.role_level !== "admin")) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
      navigate("/partner/dashboard");
    }
  }, [staffMember, authLoading, navigate, toast]);

  // Load sections from database, sync static content if empty
  const loadSections = async () => {
    setIsLoading(true);
    
    try {
      // Fetch existing sections from database
      const { data: existingSections, error: fetchError } = await supabase
        .from("tier1_policy_sections")
        .select("*")
        .order("section_number", { ascending: true });

      if (fetchError) throw fetchError;

      // If database is empty, populate it with static content
      if (!existingSections || existingSections.length === 0) {
        toast({
          title: "Initializing",
          description: "Loading policy sections into database...",
        });

        // Prepare data for insertion
        const sectionsToInsert = policyContent.map(section => ({
          section_number: section.number,
          title: section.title,
          description: section.description,
          content: { subsections: [] } // Empty subsections initially
        }));

        const { error: insertError } = await supabase
          .from("tier1_policy_sections")
          .insert(sectionsToInsert);

        if (insertError) throw insertError;

        // Fetch again after insertion
        const { data: newSections, error: refetchError } = await supabase
          .from("tier1_policy_sections")
          .select("*")
          .order("section_number", { ascending: true });

        if (refetchError) throw refetchError;

        setSections((newSections as unknown as PolicySection[]) || []);
        
        toast({
          title: "Success",
          description: "Policy sections loaded successfully",
        });
      } else {
        setSections((existingSections as unknown as PolicySection[]) || []);
      }
    } catch (error) {
      console.error("Error loading sections:", error);
      toast({
        title: "Error",
        description: "Failed to load policy sections",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  // Open dialog for new section
  const handleAddNew = () => {
    setEditingSection(null);
    form.reset({
      section_number: sections.length > 0 ? Math.max(...sections.map(s => s.section_number)) + 1 : 1,
      title: "",
      description: "",
      subsections: [{ title: "", content: "" }],
    });
    setIsDialogOpen(true);
  };

  // Open dialog for editing
  const handleEdit = (section: PolicySection) => {
    setEditingSection(section);
    form.reset({
      section_number: section.section_number,
      title: section.title,
      description: section.description,
      subsections: section.content.subsections,
    });
    setIsDialogOpen(true);
  };

  // Save section
  const onSubmit = async (data: SectionFormData) => {
    const sectionData = {
      section_number: data.section_number,
      title: data.title,
      description: data.description,
      content: { subsections: data.subsections },
    };

    if (editingSection) {
      // Update existing
      const { error } = await supabase
        .from("tier1_policy_sections")
        .update(sectionData)
        .eq("id", editingSection.id);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to update section",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Section updated successfully",
      });
    } else {
      // Create new
      const { error } = await supabase
        .from("tier1_policy_sections")
        .insert(sectionData);

      if (error) {
        toast({
          title: "Error",
          description: error.message.includes("duplicate") 
            ? "Section number already exists"
            : "Failed to create section",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Section created successfully",
      });
    }

    setIsDialogOpen(false);
    loadSections();
  };

  // Delete section
  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this section?")) return;

    const { error } = await supabase
      .from("tier1_policy_sections")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete section",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Section deleted successfully",
    });

    loadSections();
  };

  // Add/remove subsections
  const addSubsection = () => {
    const current = form.getValues("subsections");
    form.setValue("subsections", [...current, { title: "", content: "" }]);
  };

  const removeSubsection = (index: number) => {
    const current = form.getValues("subsections");
    form.setValue("subsections", current.filter((_, i) => i !== index));
  };

  // Show loading state while checking auth or fetching sections
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground">
              Tier 1 Policy Editor
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage sections of the Tier 1 Group Investment Policy
            </p>
          </div>
          <Button onClick={handleAddNew}>
            <Plus className="mr-2 h-4 w-4" />
            Add Section
          </Button>
        </div>

        <div className="grid gap-6">
          {sections.map((section) => (
            <Card key={section.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>
                      Section {section.section_number}: {section.title}
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {section.description}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(section)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(section.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {section.content.subsections.length} subsection(s)
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingSection ? "Edit Section" : "Add New Section"}
              </DialogTitle>
              <DialogDescription>
                {editingSection
                  ? "Update the section details and subsections"
                  : "Create a new policy section with subsections"}
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="section_number"
                  rules={{ required: "Section number is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Section Number</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  rules={{ required: "Description is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Subsections</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addSubsection}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Subsection
                    </Button>
                  </div>

                  {form.watch("subsections").map((_, index) => (
                    <Card key={index}>
                      <CardContent className="pt-6 space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium">Subsection {index + 1}</h4>
                          {form.watch("subsections").length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSubsection(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <FormField
                          control={form.control}
                          name={`subsections.${index}.title`}
                          rules={{ required: "Subsection title is required" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subsection Title</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`subsections.${index}.content`}
                          rules={{ required: "Subsection content is required" }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subsection Content</FormLabel>
                              <FormControl>
                                <RichTextEditor
                                  content={field.value}
                                  onChange={field.onChange}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingSection ? "Update Section" : "Create Section"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}